import { useMemo, useState, useCallback, useRef } from "react";
import {
  Tag,
  Button,
  Empty,
  Tooltip,
  Popconfirm,
  Skeleton,
  Dropdown,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  StarFilled,
  MoreOutlined,
} from "@ant-design/icons";
import GoldStandardViewerModal from "./GoldStandardViewerModal";
import { SUBTYPE_NONE } from "./SubtypeSelect";

export const CATEGORY_COLORS = {
  "Agency Decisions": "#7b1fa2",
  Appeals: "#d32f2f",
  Applications: "#0C53BB",
  "Attorneys Fees": "#388e3c",
  Codesheets: "#f9a825",
  Correspondence: "#e64a19",
  DBQs: "#0097a7",
  "DD-214 Military Admin": "#5d4037",
  "Exam Scheduling": "#6a1b9a",
  "Lay Statements": "#c2185b",
  Miscellaneous: "#616161",
  "Post-Service Medical Records": "#0288d1",
  "Service Treatment Records": "#455a64",
};

const styles = {
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "20px 0 12px",
    paddingBottom: 6,
    borderBottom: "1px solid #f0f0f0",
  },
  sectionCount: {
    fontSize: 12,
    color: "#8c8c8c",
    fontWeight: 400,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: 12,
    marginBottom: 16,
  },
  card: {
    position: "relative",
    border: "1px solid #f0f0f0",
    borderRadius: 8,
    overflow: "hidden",
    background: "#fff",
    cursor: "pointer",
    transition: "box-shadow 0.2s, border-color 0.2s, transform 0.15s",
  },
  cardHover: {
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    borderColor: "#d9d9d9",
    transform: "translateY(-1px)",
  },
  thumbnailArea: {
    height: 120,
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  thumbnailImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  docIcon: {
    fontSize: 36,
    color: "#bfbfbf",
  },
  starBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    fontSize: 14,
    color: "#faad14",
    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))",
  },
  cardBody: {
    padding: "8px 10px",
    minHeight: 52,
  },
  filename: {
    fontSize: 12,
    fontWeight: 500,
    color: "#1b2028",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: "18px",
  },
  meta: {
    fontSize: 11,
    color: "#8c8c8c",
    marginTop: 2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actionsBtn: {
    position: "absolute",
    top: 6,
    left: 6,
    opacity: 0,
    transition: "opacity 0.15s",
    background: "rgba(255,255,255,0.9)",
    borderColor: "#d9d9d9",
  },
  actionsBtnVisible: {
    opacity: 1,
  },
};

export const GoldStandardCard = ({
  item,
  onEdit,
  onRemove,
  onView,
  removeConfirmTitle = "Remove this gold standard?",
  readOnly = false,
}) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const skipNextDismiss = useRef(false);

  const handleMenuClick = useCallback(
    ({ key, domEvent }) => {
      domEvent?.stopPropagation?.();
      if (key === "edit") {
        onEdit(item);
      } else if (key === "delete") {
        skipNextDismiss.current = true;
        setConfirmOpen(true);
      }
    },
    [item, onEdit]
  );

  const handleCardClick = useCallback(() => {
    if (onView) onView(item);
  }, [item, onView]);

  const showThumbnail = item.thumbnail_url && !imgFailed;

  const menuItems = [
    { key: "edit", icon: <EditOutlined />, label: "Edit notes" },
    { key: "delete", icon: <DeleteOutlined />, label: "Remove", danger: true },
  ];

  return (
    <div
      role="button"
      tabIndex={0}
      style={{
        ...styles.card,
        ...(hovered ? styles.cardHover : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
      data-testid={`gold-card-${item.id}`}
    >
      <div style={styles.thumbnailArea}>
        {showThumbnail ? (
          <img
            src={item.thumbnail_url}
            alt={`Page 1 of ${item.filename || "document"}`}
            loading="lazy"
            onError={() => setImgFailed(true)}
            style={styles.thumbnailImg}
            data-testid="gold-card-thumbnail"
          />
        ) : (
          <FileTextOutlined style={styles.docIcon} data-testid="gold-card-icon-fallback" />
        )}
        <StarFilled style={styles.starBadge} data-testid="gold-star" />

        {!readOnly && (
        <Popconfirm
          title={removeConfirmTitle}
          open={confirmOpen}
          onConfirm={() => {
            setConfirmOpen(false);
            onRemove(item.id);
          }}
          onCancel={() => setConfirmOpen(false)}
          onOpenChange={(open) => {
            if (!open) {
              if (skipNextDismiss.current) {
                skipNextDismiss.current = false;
                return;
              }
              setConfirmOpen(false);
            }
          }}
          placement="bottomLeft"
        >
          <Dropdown
            menu={{ items: menuItems, onClick: handleMenuClick }}
            trigger={["click"]}
            placement="bottomLeft"
          >
            <Button
              size="small"
              type="default"
              icon={<MoreOutlined />}
              data-testid={`gold-card-actions-${item.id}`}
              style={{
                ...styles.actionsBtn,
                ...(hovered || focused || confirmOpen ? styles.actionsBtnVisible : {}),
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </Popconfirm>
        )}
      </div>
      <div style={styles.cardBody}>
        <Tooltip title={item.filename}>
          <div style={styles.filename} data-testid="gold-card-filename">
            {item.filename || "Untitled"}
          </div>
        </Tooltip>
        {item.subtype && (
          <Tag
            color="geekblue"
            style={{ marginTop: 6 }}
            data-testid="gold-card-subtype"
          >
            {item.subtype_label || item.subtype}
          </Tag>
        )}
        {item.category_confidence != null && (
          <div style={styles.meta}>{item.category_confidence}% confidence</div>
        )}
      </div>
    </div>
  );
};

/**
 * Category-grouped card grid for the Gold Standard Library.
 *
 * Props:
 *  goldStandards — `{ "Category Name": [...items] }` map (from the BE grouped response)
 *  selectedCategory — optional category filter
 *  selectedSubtype — optional subtype filter; SUBTYPE_NONE selects the
 *    rows carrying no subtype, which is the curation worklist
 *  searchText — freetext search string
 *  loading — whether data is being fetched
 *  onEdit — called with the item when "Edit notes" is clicked
 *  onRemove — called with the item id when "Remove" is confirmed
 *
 * Reused by the Notification Letters library, which is the same
 * curation surface teaching a different question. The generic knobs:
 *  groupColors — section-tag colors keyed by group name (falls back
 *    to CATEGORY_COLORS, then gray)
 *  removeConfirmTitle — Popconfirm wording on the card menu
 *  emptyState — node shown when the library has no rows at all
 */
const GoldStandardLibraryGrid = ({
  goldStandards = {},
  selectedCategory = null,
  selectedSubtype = null,
  searchText = "",
  loading = false,
  onEdit,
  onRemove,
  groupColors = {},
  removeConfirmTitle = "Remove this gold standard?",
  emptyState = null,
  readOnly = false,
}) => {
  const [viewerItem, setViewerItem] = useState(null);

  const handleView = useCallback((item) => {
    setViewerItem(item);
  }, []);

  const handleCloseViewer = useCallback(() => {
    setViewerItem(null);
  }, []);

  const groupedFiltered = useMemo(() => {
    const entries = selectedCategory
      ? { [selectedCategory]: goldStandards[selectedCategory] || [] }
      : goldStandards;

    const result = {};
    const lowerSearch = searchText.trim().toLowerCase();

    Object.entries(entries).forEach(([category, items]) => {
      if (!Array.isArray(items)) return;
      let filtered = lowerSearch
        ? items.filter((item) =>
            [item.filename, item.short_summary, item.notes, category]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(lowerSearch)
          )
        : items;

      if (selectedSubtype) {
        filtered = filtered.filter((item) =>
          selectedSubtype === SUBTYPE_NONE
            ? !item.subtype
            : item.subtype === selectedSubtype
        );
      }

      if (filtered.length > 0) {
        result[category] = filtered;
      }
    });

    return result;
  }, [goldStandards, selectedCategory, selectedSubtype, searchText]);

  const sortedCategories = useMemo(
    () =>
      Object.keys(groupedFiltered).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      ),
    [groupedFiltered]
  );

  const totalVisible = useMemo(
    () =>
      Object.values(groupedFiltered).reduce(
        (sum, items) => sum + items.length,
        0
      ),
    [groupedFiltered]
  );

  if (loading) {
    return (
      <div data-testid="gold-grid-loading">
        {[1, 2].map((section) => (
          <div key={section}>
            <Skeleton.Input
              active
              style={{
                width: 160,
                height: 20,
                marginTop: 20,
                marginBottom: 12,
              }}
            />
            <div style={styles.grid}>
              {[1, 2, 3, 4].map((card) => (
                <Skeleton.Node
                  key={card}
                  active
                  style={{ width: "100%", height: 172, borderRadius: 8 }}
                >
                  <FileTextOutlined style={{ fontSize: 36, color: "#f0f0f0" }} />
                </Skeleton.Node>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (totalVisible === 0) {
    const hasAnyData = Object.values(goldStandards).some(
      (items) => Array.isArray(items) && items.length > 0
    );

    let emptyFilterMsg = "No gold standards match your filters.";
    if (searchText.trim()) {
      emptyFilterMsg = "No gold standards match your search.";
    } else if (selectedCategory) {
      emptyFilterMsg = `No gold standards in "${selectedCategory}" yet.`;
    }

    if (!hasAnyData && emptyState) {
      return emptyState;
    }
    return (
      <Empty
        data-testid="gold-grid-empty"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          hasAnyData ? (
            emptyFilterMsg
          ) : (
            <span>
              No gold standard examples yet.
              <br />
              <span style={{ color: "#888", fontSize: 13 }}>
                Right-click a document on any matter and pick
                &ldquo;Categorize&hellip;&rdquo; to build your training library.
                Aim for 5&ndash;10 examples per category.
              </span>
            </span>
          )
        }
      />
    );
  }

  return (
    <div data-testid="gold-grid-container">
      {sortedCategories.map((category) => {
        const items = groupedFiltered[category];
        return (
          <div key={category} data-testid={`gold-section-${category}`}>
            <div style={styles.sectionHeader}>
              <Tag
                color={groupColors[category] || CATEGORY_COLORS[category] || "#666"}
                style={{ margin: 0 }}
              >
                {category}
              </Tag>
              <span style={styles.sectionCount}>({items.length})</span>
            </div>
            <div style={styles.grid}>
              {items.map((item, idx) => (
                <GoldStandardCard
                  key={item.id || idx}
                  item={{ ...item, _category: category }}
                  onEdit={onEdit}
                  onRemove={onRemove}
                  onView={handleView}
                  removeConfirmTitle={removeConfirmTitle}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>
        );
      })}

      <GoldStandardViewerModal
        open={viewerItem != null}
        onClose={handleCloseViewer}
        documentId={viewerItem?.document_id}
        filename={viewerItem?.filename}
        category={viewerItem?._category || viewerItem?.category}
      />
    </div>
  );
};

export default GoldStandardLibraryGrid;
